import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import useApiResource from "@pages/TicketNew/useApiResource";
import { Card, SectionLabel } from "../ui/Card";
import TextField from "../ui/TextField";
import SearchableSelectField from "../ui/SearchableSelectField";
import { formatAddress } from "../address";
import { isValidEmail, isValidPhone } from "../validation";
import AddressFields from "./AddressFields";
import DeliveryAddressField from "./DeliveryAddressField";

// Customers (`isCustomer`) only pick the retailer and store; contact and
// address fields are retailer-only.
export default function RetailSection({
  form,
  protectedFetch,
  attempted = false,
  isCustomer = false,
}) {
  const { values, setField, setValues } = form;
  const retailers = useApiResource(
    protectedFetch,
    apiEndpoints.portal.retailers(),
    null,
  );
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const showEmailError = emailTouched || attempted;
  const emailError = !showEmailError
    ? undefined
    : values.retailerEmail === ""
      ? undefined
      : !isValidEmail(values.retailerEmail)
        ? "Enter a valid email address"
        : undefined;

  const showPhoneError = phoneTouched || attempted;
  const phoneError = !showPhoneError
    ? undefined
    : values.retailerPhone === ""
      ? undefined
      : !isValidPhone(values.retailerPhone)
        ? "Enter a valid phone number"
        : undefined;

  const retailerList = retailers.data ?? [];
  const retailerOptions = retailerList.map((retailer) => ({
    value: retailer.name,
    label: retailer.name,
  }));

  const selectedRetailerId = retailerList.find(
    (retailer) => retailer.name === values.retailer,
  )?.id;
  const storeLocations = useApiResource(
    protectedFetch,
    apiEndpoints.portal.storeLocations(selectedRetailerId),
    null,
    { skip: !selectedRetailerId },
  );

  const storeOptions = (storeLocations.data ?? []).map((store) => ({
    value: store.name,
    label: store.name,
  }));

  return (
    <Card radius="panel">
      <span className="rma-nc-retail-badge">Retail</span>

      <div className="rma-nc-grid-2">
        <SearchableSelectField
          label="Retailer"
          placeholder="Search for a retailer…"
          value={values.retailer}
          options={retailerOptions}
          isLoading={retailers.loading}
          onChange={(value) => setValues({ ...values, retailer: value, store: "" })}
        />
        <SearchableSelectField
          label="Store"
          placeholder="Search for a store…"
          value={values.store}
          options={storeOptions}
          isLoading={storeLocations.loading}
          isDisabled={!selectedRetailerId}
          onChange={(value) => setField("store", value)}
        />
        {!isCustomer && (
          <>
            <div>
              <TextField
                label="Phone"
                size="md"
                type="tel"
                placeholder="e.g. 612 9123 4567"
                value={values.retailerPhone}
                onChange={(event) => setField("retailerPhone", event.target.value)}
                onBlur={() => setPhoneTouched(true)}
                className={phoneError ? "rma-nc-control--error" : undefined}
              />
              {phoneError && <p className="rma-nc-error-text">{phoneError}</p>}
            </div>
            <div>
              <TextField
                label="Email"
                size="md"
                type="email"
                placeholder="e.g. name@harveynorman.com.au"
                hint="Note: This email address will be used as the primary contact for receiving notifications."
                value={values.retailerEmail}
                onChange={(event) => setField("retailerEmail", event.target.value)}
                onBlur={() => setEmailTouched(true)}
                className={emailError ? "rma-nc-control--error" : undefined}
              />
              {emailError && <p className="rma-nc-error-text">{emailError}</p>}
            </div>
          </>
        )}
      </div>

      {!isCustomer && (
        <>
          <SectionLabel>Address</SectionLabel>
          <AddressFields
            value={values.retailerAddress}
            onChange={(address) => setField("retailerAddress", address)}
            protectedFetch={protectedFetch}
            attempted={attempted}
          />

          <DeliveryAddressField
            className="rma-nc-mt-18"
            sameAsAbove={values.deliverySameAsAbove}
            onSameAsAboveChange={(checked) => setField("deliverySameAsAbove", checked)}
            aboveAddress={formatAddress(values.retailerAddress, { includeCountry: false })}
            value={values.deliveryAddress}
            onChange={(address) => setField("deliveryAddress", address)}
            protectedFetch={protectedFetch}
            attempted={attempted}
          />
        </>
      )}
    </Card>
  );
}
